import { fetcher } from '@data/utils/api/fetcher/fetcher';
import formatDate from '@data/utils/format-date';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { z } from 'zod';

// ── Metric types ───────────────────────────────────────────────────────────────

/**
 * The organization-wide metrics the reporting service exposes to admins.
 *
 * A separate set from the teacher `MetricV2Type`s: these read organization
 * roll-ups rather than a teacher's roster, and the service rejects a teacher
 * metric type on this route.
 */
export type AdminMetricType =
  | 'ACTIVE_STUDENTS'
  | 'ACTIVE_EDUCATORS'
  | 'ADMIN_STUDENT_SESSIONS'
  | 'ADMIN_STUDENT_MINUTES'
  | 'ADMIN_ACTIVITIES_COMPLETED'
  | 'ADMIN_AVG_ACTIVITY_SCORE'
  | 'ADMIN_CONTENT_COMPLETIONS'
  | 'ADMIN_CONTENT_VIEWS'
  | 'ADMIN_TOTAL_EDUCATOR_STUDENT_SESSIONS'
  | 'ADMIN_TOTAL_EDUCATOR_STUDENT_MINUTES'
  | 'TOTAL_EDUCATOR_SESSIONS'
  | 'TOTAL_EDUCATOR_MINUTES';

/** Rendered as a percentage rather than a count. */
const PERCENT_ADMIN_METRICS: AdminMetricType[] = ['ADMIN_AVG_ACTIVITY_SCORE'];

// ── Response ───────────────────────────────────────────────────────────────────

const adminMetricValueShape = z.object({
  metric: z.string(),
  /**
   * Null is meaningful: COUNT metrics report "0" when there is nothing to
   * count, while SUM and AVERAGE metrics report null, so "no data" stays
   * distinguishable from a total that really is zero.
   */
  value: z.string().nullish(),
  label: z.string(),
  toolTip: z.string().nullish(),
});

const adminMetricsShape = z.array(
  z.object({
    edapp: z.string(),
    metric: z.array(adminMetricValueShape).nullish(),
  })
);

/** Normalized to the shape the shared `Metrics` component renders. */
export interface AdminMetric {
  metricType: AdminMetricType;
  value: string | null;
  label: string;
  toolTip?: string;
  format: 'number' | 'percent';
}

// ── Request ────────────────────────────────────────────────────────────────────

export interface AdminMetricsRequest {
  /**
   * Every organization the figures should cover.
   *
   * A list rather than one id because the filter scopes to any number of
   * organizations: one when only one is available, the chosen few when some are
   * picked, and all of them when none are — the same way the teacher reports
   * treat an empty class selection as every class.
   */
  orgIds: string[];
  /** Narrows the roll-up to these educators; omitted means every educator. */
  teacherIds?: string[];
  metricTypes: AdminMetricType[];
  edApps: string[];
  startDate: Date;
  endDate: Date;
}

export interface UseAdminMetricsApiProps extends AdminMetricsRequest {
  enabled?: boolean;
}

export const getAdminMetricsApi = async (
  request: AdminMetricsRequest
): Promise<AdminMetric[]> => {
  const { orgIds, teacherIds, metricTypes, edApps, startDate, endDate } =
    request;

  const response = await fetcher('/metrics/admin', {
    service: '/data',
    version: '/v2',
    method: 'POST',
    body: JSON.stringify({
      orgIds,
      ...(teacherIds && teacherIds.length > 0 ? { teacherIds } : {}),
      metricTypes,
      edApps,
      startDate: formatDate(startDate),
      endDate: formatDate(endDate, { boundary: 'end' }),
    }),
  });

  const byEdApp = adminMetricsShape.parse(response);

  // The response carries one entry per requested edApp. A product may be asked
  // for under more than one code (LITPRO also reports as LP_RMMVP), so the
  // entries are merged and the first value seen for a metric wins.
  const seen = new Map<string, AdminMetric>();
  byEdApp.forEach((entry) => {
    (entry.metric ?? []).forEach((value) => {
      if (seen.has(value.metric)) return;
      const metricType = value.metric as AdminMetricType;
      seen.set(value.metric, {
        metricType,
        value: value.value ?? null,
        label: value.label,
        format: PERCENT_ADMIN_METRICS.includes(metricType)
          ? 'percent'
          : 'number',
        ...(value.toolTip ? { toolTip: value.toolTip } : {}),
      });
    });
  });

  // Ordered as requested, so the tiles keep the order the page declares.
  return metricTypes
    .map((metricType) => seen.get(metricType))
    .filter((metric): metric is AdminMetric => !!metric);
};

// ── Hook ───────────────────────────────────────────────────────────────────────

export function adminMetricsQueryKey(request: AdminMetricsRequest) {
  const { orgIds, teacherIds, metricTypes, edApps, startDate, endDate } =
    request;
  return [
    'admin-metrics',
    {
      orgIds,
      teacherIds: teacherIds ?? null,
      metricTypes,
      edApps,
      startDate: formatDate(startDate),
      endDate: formatDate(endDate, { boundary: 'end' }),
    },
  ] as const;
}

/** Organization-wide metrics for one product, for the admin Overview. */
export function useAdminMetricsApi({
  enabled,
  ...request
}: UseAdminMetricsApiProps): UseQueryResult<AdminMetric[], Error> {
  return useQuery({
    queryKey: adminMetricsQueryKey(request),
    queryFn: () => getAdminMetricsApi(request),
    enabled:
      enabled !== false &&
      request.metricTypes.length > 0 &&
      request.edApps.length > 0 &&
      request.orgIds.length > 0,
    staleTime: 3 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}
